/* verqo — Sub-page building blocks (preview build)
   Reusable atoms for all product/solutions/company sub-pages.
   Ramp/every.io flavor: generous spacing, editorial typography, lime accents.
*/
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, CircleDot, ChevronDown, Sparkles,
} from "lucide-react";
import { PreviewNavHeader } from "./PreviewNavHeader";
import { PreviewFooter } from "./PreviewFooter";

/* Chrome-style browser frame around a live product screenshot.
   Adds a subtle top bar with 3 traffic-light dots + a URL pill. */
export function BrowserFrame({ src, alt, url, className = "" }) {
  return (
    <div className={`relative rounded-xl overflow-hidden border border-slate-200 bg-white shadow-[0_28px_80px_-24px_rgba(15,23,42,0.24)] ${className}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-300"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-300"></span>
        </div>
        {url && (
          <div className="ml-3 flex-1 rounded-md bg-white border border-slate-200 px-2.5 py-0.5 text-[10.5px] text-slate-500 font-mono truncate">
            {url}
          </div>
        )}
      </div>
      <img src={src} alt={alt || "product screenshot"} className="block w-full h-auto" loading="lazy" />
    </div>
  );
}

/* Layout wrapper for every sub-page */
export function SubPageLayout({ children, modeOverride }) {
  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ ["--brand-fg"]: "#0c1612", ["--brand-accent"]: "#dcff3a" }}>
      <PreviewNavHeader modeOverride={modeOverride} />
      <main>{children}</main>
      <PreviewFooter modeOverride={modeOverride} />
    </div>
  );
}

export const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-lime-700" data-testid="page-eyebrow">
    <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500" />
    {children}
  </div>
);

export const Reveal = ({ children, delay = 0, y = 24 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

/* Hero — split, big headline + right visual */
export function HeroBlock({ eyebrow, title, subtitle, ctas = [], visual }) {
  return (
    <section className="relative pt-20 md:pt-28 pb-16 md:pb-20 px-6 md:px-10 max-w-[1280px] mx-auto" data-testid="page-hero">
      <div className={`grid ${visual ? "grid-cols-1 lg:grid-cols-[1.15fr_1fr]" : "grid-cols-1"} gap-12 lg:gap-16 items-center`}>
        <div>
          <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h1
              className="mt-6 text-[36px] sm:text-[48px] md:text-[64px] leading-[1.02] tracking-tight font-semibold text-slate-900"
              data-testid="page-headline"
            >
              {title}
            </h1>
          </Reveal>
          {subtitle && (
            <Reveal delay={0.18}>
              <p className="mt-6 max-w-xl text-[15.5px] md:text-[17px] leading-relaxed text-slate-600" data-testid="page-subhead">
                {subtitle}
              </p>
            </Reveal>
          )}
          {ctas.length > 0 && (
            <Reveal delay={0.28}>
              <div className="mt-9 flex flex-wrap gap-3 items-center" data-testid="page-hero-ctas">
                {ctas.map((c, i) => (
                  <a
                    key={c.href}
                    href={c.href}
                    data-testid={`page-hero-cta-${i}`}
                    className={
                      c.variant === "ghost"
                        ? "inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50 transition-colors"
                        : "group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-opacity bg-[#0c1612]"
                    }
                  >
                    {c.label}
                    {c.variant !== "ghost" && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                  </a>
                ))}
              </div>
            </Reveal>
          )}
        </div>
        {visual && (
          <Reveal delay={0.16} y={32}>
            <div data-testid="page-hero-visual">{visual}</div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

/* Logo strip — social proof band */
export function LogoStrip({ items, note }) {
  return (
    <section className="relative py-10 md:py-14 border-y border-slate-100 bg-slate-50/40" data-testid="page-logos">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        {note && <div className="text-center text-[11px] uppercase tracking-[0.28em] text-slate-500 mb-7">{note}</div>}
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5">
          {items.map((n, i) => (
            <span key={i} className="text-[15px] md:text-[17px] text-slate-500 font-medium select-none" style={{ fontFamily: n.font || "Georgia, serif", fontStyle: n.italic ? "italic" : "normal" }}>
              {n.name || n}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Feature grid — 3 or 4 columns, icon+title+desc */
export function FeatureGrid({ heading, sub, features }) {
  return (
    <section className="relative py-20 md:py-24 px-6 md:px-10 max-w-[1280px] mx-auto" data-testid="page-features">
      {(heading || sub) && (
        <div className="max-w-3xl">
          {heading && (
            <Reveal>
              <h2 className="text-[28px] md:text-[42px] font-semibold tracking-tight leading-[1.05] text-slate-900" data-testid="page-features-heading">
                {heading}
              </h2>
            </Reveal>
          )}
          {sub && (
            <Reveal delay={0.08}>
              <p className="mt-5 text-[15px] md:text-[16px] leading-relaxed text-slate-600 max-w-2xl">
                {sub}
              </p>
            </Reveal>
          )}
        </div>
      )}
      <div className={`mt-12 grid grid-cols-1 md:grid-cols-2 ${features.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-5`}>
        {features.map((f, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 hover:border-lime-400 hover:-translate-y-0.5 transition-all" data-testid={`page-feature-${i}`}>
              {f.icon && (
                <div className="w-10 h-10 rounded-lg bg-slate-900 grid place-items-center mb-4">
                  <f.icon className="w-4.5 h-4.5" style={{ color: "var(--brand-accent)" }} strokeWidth={1.8} />
                </div>
              )}
              <div className="text-[15px] font-semibold text-slate-900">{f.title}</div>
              <div className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{f.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* Big alternating block — image + copy */
export function AltBlocks({ blocks }) {
  return (
    <section className="relative py-16 md:py-24 px-6 md:px-10 max-w-[1280px] mx-auto" data-testid="page-altblocks">
      <div className="space-y-20 md:space-y-28">
        {blocks.map((b, i) => (
          <Reveal key={i} delay={0.05}>
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <div>
                {b.eyebrow && <Eyebrow>{b.eyebrow}</Eyebrow>}
                <h3 className="mt-4 text-[26px] md:text-[36px] font-semibold tracking-tight leading-[1.08] text-slate-900" data-testid={`page-altblock-title-${i}`}>
                  {b.title}
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-slate-600 max-w-xl" data-testid={`page-altblock-body-${i}`}>
                  {b.body}
                </p>
                {b.bullets && (
                  <ul className="mt-5 space-y-2.5">
                    {b.bullets.map((bl, bi) => (
                      <li key={bi} className="flex items-start gap-2 text-[13.5px] text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 flex-shrink-0" />
                        <span>{bl}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {b.cta && (
                  <a href={b.cta.href} className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-lime-800 hover:text-slate-900 transition-colors">
                    {b.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
              <div>
                {b.visual || (
                  <div className="relative rounded-2xl aspect-[4/3] overflow-hidden border border-slate-200 bg-gradient-to-br from-slate-50 via-lime-50/40 to-slate-100">
                    <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                      style={{ backgroundImage: "linear-gradient(rgba(15,23,42,1) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,1) 1px, transparent 1px)", backgroundSize: "24px 24px" }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-2xl bg-white/70 backdrop-blur-sm border border-slate-200 grid place-items-center shadow-sm">
                        <div className="w-6 h-6 rounded-md bg-lime-300"></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* Metrics bar */
export function MetricsBar({ metrics }) {
  return (
    <section className="relative py-16 md:py-20 px-6 md:px-10 bg-slate-900 text-white" data-testid="page-metrics">
      <div className="max-w-[1280px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-10">
        {metrics.map((m, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <div>
              <div className="text-[36px] md:text-[52px] font-semibold tracking-tight" style={{ color: "var(--brand-accent)" }} data-testid={`page-metric-${i}`}>
                {m.value}
              </div>
              <div className="mt-2 text-[13px] text-white/70 leading-relaxed max-w-[200px]">{m.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* Quote block */
export function Quote({ quote, author, role, company }) {
  return (
    <section className="relative py-20 md:py-28 px-6 md:px-10 max-w-[900px] mx-auto text-center" data-testid="page-quote">
      <Reveal>
        <Sparkles className="w-6 h-6 text-lime-500 mx-auto mb-6" />
        <blockquote className="text-[22px] md:text-[32px] leading-[1.25] font-medium tracking-tight text-slate-900">
          &ldquo;{quote}&rdquo;
        </blockquote>
        <div className="mt-8 text-[13px] text-slate-600">
          <div className="font-semibold text-slate-900">{author}</div>
          <div>{role}{company && ` · ${company}`}</div>
        </div>
      </Reveal>
    </section>
  );
}

/* FAQ accordion */
export function FAQ({ heading = "Common questions", items }) {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative py-20 md:py-28 px-6 md:px-10 max-w-[900px] mx-auto" data-testid="page-faq">
      <Reveal>
        <h2 className="text-[26px] md:text-[36px] font-semibold tracking-tight text-slate-900 text-center mb-10">
          {heading}
        </h2>
      </Reveal>
      <div className="divide-y divide-slate-200 border-y border-slate-200">
        {items.map((it, i) => {
          const isOpen = open === i;
          return (
            <div key={i} data-testid={`page-faq-item-${i}`}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full flex items-center justify-between gap-6 py-5 text-left group"
              >
                <span className="text-[15px] md:text-[17px] font-semibold text-slate-900 group-hover:text-lime-800 transition-colors">
                  {it.q}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="pb-6 text-[14px] text-slate-600 leading-relaxed max-w-2xl">
                  {it.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* Screenshot showcase — tabbed viewer for product-page screenshots
   items: [{ label, src, alt, url, caption }]
   Shows big BrowserFrame + tab bar for switching between views.
*/
export function ScreenshotShowcase({ heading, sub, items, cta }) {
  const [idx, setIdx] = useState(0);
  if (!items || items.length === 0) return null;
  const active = items[idx];
  return (
    <section className="relative py-20 md:py-28 px-6 md:px-10 max-w-[1280px] mx-auto" data-testid="page-screenshots">
      {(heading || sub) && (
        <div className="max-w-3xl mb-10 md:mb-14">
          {heading && (
            <Reveal>
              <h2 className="text-[28px] md:text-[42px] font-semibold tracking-tight leading-[1.05] text-slate-900">
                {heading}
              </h2>
            </Reveal>
          )}
          {sub && (
            <Reveal delay={0.08}>
              <p className="mt-5 text-[15px] md:text-[16px] leading-relaxed text-slate-600 max-w-2xl">
                {sub}
              </p>
            </Reveal>
          )}
        </div>
      )}
      {items.length > 1 && (
        <div className="mb-6 inline-flex items-center gap-1 p-1 rounded-full bg-slate-100 border border-slate-200" data-testid="screenshot-tabs">
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              data-testid={`screenshot-tab-${i}`}
              className={`px-4 py-1.5 rounded-full text-[12.5px] font-semibold transition-all ${
                i === idx ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
      <Reveal>
        <BrowserFrame src={active.src} alt={active.alt} url={active.url} />
      </Reveal>
      {active.caption && (
        <div className="mt-5 text-[12.5px] text-slate-500 leading-relaxed max-w-2xl">
          {active.caption}
        </div>
      )}
      {cta && (
        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap gap-3 items-center" data-testid="page-screenshots-cta">
            {cta.primary && (
              <a
                href={cta.primary.href}
                data-testid="page-screenshots-cta-primary"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-opacity bg-[#0c1612]"
              >
                {cta.primary.label}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            )}
            {cta.secondary && (
              <a
                href={cta.secondary.href}
                data-testid="page-screenshots-cta-secondary"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-900 text-sm hover:bg-slate-50 transition-colors"
              >
                {cta.secondary.label}
              </a>
            )}
          </div>
        </Reveal>
      )}
    </section>
  );
}

/* Related pages strip */
export function RelatedPages({ heading = "Explore more", items }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="relative py-16 md:py-20 px-6 md:px-10 max-w-[1280px] mx-auto" data-testid="page-related">
      <div className="text-[10.5px] uppercase tracking-[0.24em] text-slate-500 font-semibold mb-6">
        {heading}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <Link
            key={it.href}
            to={it.href}
            data-testid={`page-related-${i}`}
            className="group rounded-xl border border-slate-200 bg-white p-5 hover:border-lime-400 hover:-translate-y-0.5 transition-all"
          >
            <div className="text-[14px] font-semibold text-slate-900">{it.label}</div>
            <div className="mt-1.5 text-[12.5px] text-slate-500 leading-relaxed">{it.desc}</div>
            <div className="mt-3 inline-flex items-center gap-1 text-[12px] text-lime-800 font-semibold">
              Read more <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* Final CTA banner */
export function CTABanner({ title, subtitle, primary, secondary }) {
  return (
    <section className="relative py-16 md:py-24 px-6 md:px-10 max-w-[1280px] mx-auto" data-testid="page-cta">
      <div className="relative overflow-hidden rounded-3xl bg-[#0c1612] px-8 md:px-16 py-14 md:py-20">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}
        />
        <div className="relative max-w-2xl">
          <h3 className="text-[28px] md:text-[44px] font-semibold tracking-tight leading-[1.08] text-white" data-testid="page-cta-title">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-4 text-[15px] md:text-[16px] text-white/70 leading-relaxed">{subtitle}</p>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            {primary && (
              <a
                href={primary.href}
                data-testid="page-cta-primary"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-slate-900 text-sm font-semibold hover:opacity-90 transition-opacity"
                style={{ background: "var(--brand-accent)" }}
              >
                {primary.label} <ArrowRight className="w-4 h-4" />
              </a>
            )}
            {secondary && (
              <a
                href={secondary.href}
                data-testid="page-cta-secondary"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/25 text-white text-sm hover:bg-white/5 transition-colors"
              >
                {secondary.label}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
